import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import apiClient from "../apiClient";
import { formatMoney } from "../formatMoney";
import PageContext from "../PageContext.jsx";

const TransactionsAdd = () => {
  const [transactionTypes, setTransactionTypes] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [existingTransactions, setExistingTransactions] = useState([]);
  const [defaultCurrency, setDefaultCurrency] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [duplicateSignature, setDuplicateSignature] = useState("");
  const [subcategoryInput, setSubcategoryInput] = useState("");
  const sortedTransactionTypes = useMemo(
    () => [...transactionTypes].sort((a, b) => (a.Description || "").localeCompare(b.Description || "")),
    [transactionTypes]
  );
  const sortedSubcategories = useMemo(
    () => [...subcategories].sort((a, b) => (a.SubcategoryDescription || "").localeCompare(b.SubcategoryDescription || "")),
    [subcategories]
  );
  const sortedPeriods = useMemo(
    () => [...periods].sort((a, b) => (a.Description || "").localeCompare(b.Description || "")),
    [periods]
  );

  const [transaction, setTransaction] = useState({
    Description: "",
    Amount: "",
    TransactionTypeID: "",
    SubcategoryId: "",
    Date: "",
    PeriodId: "",
  });

  useEffect(() => {
    const fetchDependencies = async () => {
      try {
        const [typesRes, subcategoriesRes, periodsRes, currenciesRes] = await Promise.all([
          apiClient.get("/transactiontypes"),
          apiClient.get("/subcategories"),
          apiClient.get("/periods"),
          apiClient.get("/currencies"),
        ]);

        const types = Array.isArray(typesRes.data) ? typesRes.data : [];
        const subs = Array.isArray(subcategoriesRes.data) ? subcategoriesRes.data : [];
        const periodRows = Array.isArray(periodsRes.data) ? periodsRes.data : [];
        const currencyRows = Array.isArray(currenciesRes.data) ? currenciesRes.data : [];

        const sortedTypes = [...types].sort((a, b) => (a.Description || "").localeCompare(b.Description || ""));
        const sortedSubs = [...subs].sort((a, b) => (a.SubcategoryDescription || "").localeCompare(b.SubcategoryDescription || ""));
        const sortedPeriodRows = [...periodRows].sort((a, b) => (a.Description || "").localeCompare(b.Description || ""));
        const defaultPeriod = periodRows.find((row) => Number(row.ActiveYesNo) === 1) || periodRows[0];

        setTransactionTypes(sortedTypes);
        setSubcategories(sortedSubs);
        setPeriods(sortedPeriodRows);
        setDefaultCurrency(currencyRows.find((row) => Number(row.IsDefault))?.Description || "");

        setTransaction((prev) => ({
          ...prev,
          TransactionTypeID: sortedTypes[0]?.TransactionTypeID ?? "",
          SubcategoryId: sortedSubs[0]?.SubcategoryId ?? "",
          PeriodId: defaultPeriod?.PeriodId ?? "",
        }));
        setSubcategoryInput(sortedSubs[0]?.SubcategoryDescription ?? "");
      } catch (err) {
        setError("Failed to load transaction dependencies.");
      } finally {
        setLoading(false);
      }
    };

    fetchDependencies();
  }, []);

  useEffect(() => {
    const fetchExistingTransactions = async () => {
      if (!transaction.PeriodId) {
        setExistingTransactions([]);
        return;
      }

      setLoadingTransactions(true);
      try {
        const res = await apiClient.get("/transactions", { params: { periodId: transaction.PeriodId } });
        setExistingTransactions(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        setExistingTransactions([]);
      } finally {
        setLoadingTransactions(false);
      }
    };

    fetchExistingTransactions();
  }, [transaction.PeriodId]);

  const handleChange = (e) => {
    setTransaction((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError("");
    setSuccess("");
    setDuplicateSignature("");
  };

  const handleSubcategoryInputChange = (e) => {
    const nextValue = e.target.value;
    setSubcategoryInput(nextValue);
    const exactMatch = sortedSubcategories.find(
      (item) => String(item.SubcategoryDescription || "").toLowerCase() === nextValue.trim().toLowerCase()
    );
    setTransaction((prev) => ({
      ...prev,
      SubcategoryId: exactMatch ? String(exactMatch.SubcategoryId) : "",
    }));
    setError("");
    setSuccess("");
    setDuplicateSignature("");
  };

  const selectedSubcategory = sortedSubcategories.find(
    (item) => String(item.SubcategoryId) === String(transaction.SubcategoryId)
  );

  const getDuplicateSignature = () => {
    const normalizedAmount = Number(transaction.Amount || 0).toFixed(2);
    const normalizedDate = String(transaction.Date || "");
    const normalizedCategory = String(selectedSubcategory?.CategoryDescription || "").trim().toLowerCase();

    return `${normalizedAmount}::${normalizedCategory}::${normalizedDate}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !transaction.Description?.trim() ||
      transaction.Amount === "" ||
      Number.isNaN(Number(transaction.Amount)) ||
      !transaction.TransactionTypeID ||
      !transaction.SubcategoryId ||
      !transaction.Date ||
      !transaction.PeriodId
    ) {
      setError("Please fill in all required fields.");
      return;
    }

    if (loadingTransactions) {
      setError("Still checking existing transactions for duplicates. Please try again in a moment.");
      return;
    }

    const nextDuplicateSignature = getDuplicateSignature();
    const hasPossibleDuplicate = existingTransactions.some((row) => (
      Number(row.Amount || 0).toFixed(2) === Number(transaction.Amount || 0).toFixed(2) &&
      String(row.Date || "") === String(transaction.Date || "") &&
      String(row.Category || "").trim().toLowerCase() === String(selectedSubcategory?.CategoryDescription || "").trim().toLowerCase()
    ));

    if (hasPossibleDuplicate && duplicateSignature !== nextDuplicateSignature) {
      setDuplicateSignature(nextDuplicateSignature);
      setError("Possible duplicate entry: another transaction with the same amount, category, and date already exists. Submit again to confirm you want to save it.");
      setSuccess("");
      return;
    }

    try {
      await apiClient.post("/transactions", transaction);
      setError("");
      setSuccess("Transaction saved.");
      setDuplicateSignature("");
      setExistingTransactions((current) => [
        ...current,
        {
          Amount: Number(transaction.Amount || 0),
          Date: transaction.Date,
          Category: selectedSubcategory?.CategoryDescription || "",
        },
      ]);
      setTransaction((prev) => ({
        ...prev,
        Description: "",
        Amount: "",
        Date: "",
      }));
    } catch (err) {
      setError("Could not save transaction. Please try again.");
      setSuccess("");
    }
  };

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Enter Transaction</h1>
          <p className="page-subtitle">
            <Link to="/">Home</Link> / Transactions / Add
          </p>
        </div>
      </header>
      <PageContext
        items={[
          { label: "Home", to: "/" },
          { label: "Transactions", to: "/Transactions" },
          { label: "Enter Transaction" },
        ]}
        note="Capture a dated transaction and assign it to the correct period and subcategory."
      />

      <section className="panel form-card">
        {loading && <p className="notice notice-muted">Loading form data...</p>}
        {!loading && (
          <div className="form">
            <label htmlFor="transaction-description">Description</label>
            <input
              id="transaction-description"
              type="text"
              name="Description"
              placeholder="e.g. Supermarket groceries"
              value={transaction.Description}
              onChange={handleChange}
            />

            <label htmlFor="transaction-type">Transaction Type</label>
            <select
              id="transaction-type"
              name="TransactionTypeID"
              value={transaction.TransactionTypeID}
              onChange={handleChange}
            >
              {sortedTransactionTypes.map((item) => (
                <option key={item.TransactionTypeID} value={item.TransactionTypeID}>
                  {item.Description}
                </option>
              ))}
            </select>

            <label htmlFor="transaction-amount">Amount</label>
            <input
              id="transaction-amount"
              type="number"
              step="0.01"
              name="Amount"
              placeholder="e.g. -45.50 (expense) or 1200.00 (income)"
              value={transaction.Amount}
              onChange={handleChange}
            />
            <div className="form-value-preview">
              Display: {formatMoney(transaction.Amount || 0, defaultCurrency)}
            </div>

            <label htmlFor="transaction-subcategory">Subcategory</label>
            <input
              id="transaction-subcategory"
              type="text"
              list="transaction-subcategory-options"
              value={subcategoryInput}
              onChange={handleSubcategoryInputChange}
              placeholder="Type or choose a subcategory"
            />
            <datalist id="transaction-subcategory-options">
              {sortedSubcategories.map((item) => (
                <option key={item.SubcategoryId} value={item.SubcategoryDescription}>
                  {item.CategoryDescription}
                </option>
              ))}
            </datalist>
            <div className="form-value-preview">
              Category: {selectedSubcategory?.CategoryDescription || "Select a valid subcategory"}
            </div>
            {loadingTransactions && (
              <div className="form-value-preview">Checking existing transactions for duplicates...</div>
            )}

            <label htmlFor="transaction-date">Date</label>
            <input
              id="transaction-date"
              type="date"
              name="Date"
              value={transaction.Date}
              onChange={handleChange}
            />

            <label htmlFor="transaction-period">Period</label>
            <select
              id="transaction-period"
              name="PeriodId"
              value={transaction.PeriodId}
              onChange={handleChange}
            >
              {sortedPeriods.map((item) => (
                <option key={item.PeriodId} value={item.PeriodId}>
                  {item.Description}
                </option>
              ))}
            </select>

            <div className="form-actions">
              <button className="btn" onClick={handleSubmit}>
                Save Transaction
              </button>
              <Link className="btn-link" to="/">
                Back Home
              </Link>
            </div>

            {error && <p className="notice notice-error">{error}</p>}
            {success && <p className="notice notice-muted">{success}</p>}
          </div>
        )}
      </section>
    </div>
  );
};

export default TransactionsAdd;
